import React, { useEffect, useMemo } from 'react';
import { Button, Container } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { IconButton } from '@mui/material';

import CompanyHeader from './components/CompanyHeader';

import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import { ClientFormValuesPestCompany, clientPostCompany } from 'api/clients/types';
import MLTextArea from 'components/Input/TextArea';
import { countries } from 'api/clients/country-list';
import useCreatePestCompany from 'hooks/Clients/useCreatePestCompany';
import { checkIpClient } from 'api/clients/checkIpClient';

interface PestCompanyModalProps {
  setOpenModal: (isOpen: boolean) => void;
}

function PestCompanyModal({ setOpenModal }: PestCompanyModalProps) {
  const { t } = useTranslation();
  const { createNewCompanyPest } = useCreatePestCompany();
  const queryClient = useQueryClient();

  const { handleSubmit, control, setValue, setError } = useForm<ClientFormValuesPestCompany>({
    resolver: zodResolver(clientPostCompany),
    defaultValues: {
      name: '',
      EDRPOU: '',
      ownership_form: '',
      country: '',
      city_text: '',
      zip_code: '',
      street: '',
      building_no: '',
      apartment_no: '',
      logo: undefined,
    },
  });
  const handlerError = (error: any) => {
    if (error.response && error.response.data) {
      const data = error.response.data;
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          setError(key as keyof ClientFormValuesPestCompany, { message: data[key][0] });
        }
      }
    } else {
      enqueueSnackbar(t('ClientsDashboard.dashboard.error'), { variant: 'error' });
    }
  };
  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: "Don't have countries" }];
    }
    return [{ value: '', label: 'Select Country' }].concat(
      countries.map(country => {
        return {
          value: String(country.id),
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, []);

  const createPestCompany = (data: ClientFormValuesPestCompany) => {
    const clientData: ClientFormValuesPestCompany = {
      name: data.name,
      ownership_form: data.ownership_form || '',
      description: data.description || '',
      logo: data.logo,
      EDRPOU: data.EDRPOU || '',
      zip_code: data.zip_code || '',
      country: data.country,
      city_text: data.city_text,
      street: data.street,
      building_no: data.building_no,
      apartment_no: data.apartment_no || '',
    };

    createNewCompanyPest(
      { client: clientData },
      {
        onSuccess: () => {
          setOpenModal(false);

          enqueueSnackbar(t('ClientsDashboard.is_active'), {
            variant: 'warning',
            persist: true,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            style: {
              whiteSpace: 'nowrap',
              flexWrap: 'nowrap',
              minWidth: 'max-content',
              alignItems: 'center',
              opacity: 0.5,
            },
            action: snackbarId => (
              <IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackbarId)}>
                <i className="ri-close-line" style={{ fontSize: '20px' }} />
              </IconButton>
            ),
          });

          queryClient.invalidateQueries(['pest-company']);
        },
        onError: (error: any) => {
          handlerError(error);

          enqueueSnackbar(t('ClientsDashboard.dashboard.error'), {
            variant: 'error',
            persist: true,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            style: {
              whiteSpace: 'nowrap',
              flexWrap: 'nowrap',
              minWidth: 'max-content',
              alignItems: 'center',
              opacity: 0.5,
            },
            action: snackbarId => (
              <IconButton size="small" color="inherit" onClick={() => closeSnackbar(snackbarId)}>
                <i className="ri-close-line" style={{ fontSize: '20px' }} />
              </IconButton>
            ),
          });

          console.error('Error creating client:', error);
        },
      },
    );
  };
  useEffect(() => {
    const checkServiceProvider = async () => {
      try {
        const getIpClient = await checkIpClient();
        if (getIpClient && getIpClient.country_name) {
          const foundCountry = countries.find(c => c.countryName === getIpClient.country_name);

          if (foundCountry) {
            setValue('country', String(foundCountry.id));
          }
        }
      } catch (error) {
        console.error('Error fetching service provider status or payed_till:', error);
      }
    };

    // Call the async function
    checkServiceProvider();
  }, [setValue]);
  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );
  return (
    <>
      <Container fluid>
        <CompanyHeader />
        <div className="card">
          <div className="card-header">
            <form className="row g-3" onSubmit={handleSubmit(createPestCompany)}>
              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="name"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      placeholder="Trojan System"
                      label={t('Modals.manage-client.name')}
                      error={error?.message}
                      required
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="EDRPOU"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      placeholder="113442121"
                      label={t('Modals.manage-client.EDRPOU')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="ownership_form"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      placeholder="owenership form"
                      label={t('Modals.manage-client.ownership_form')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <MLSelect
                      {...field}
                      label={t('Modals.manage-client.country')}
                      error={error?.message}
                      options={countryList}
                      formatOptionLabel={formatCountryOption}
                      value={String(field.value || '')}
                      onChange={val => field.onChange(val || '')}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="city_text"
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.city')}
                      placeholder="New York"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="zip_code"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.zip')}
                      placeholder="10001"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="street"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.street')}
                      placeholder="Houston"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="building_no"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.building_no')}
                      placeholder="0"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-4">
                <Controller
                  control={control}
                  name="apartment_no"
                  render={({ field, fieldState: { error } }) => (
                    <MLInput
                      type="text"
                      label={t('Modals.manage-client.apartment_no')}
                      placeholder="12"
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <div className="col-12">
                <h6 className="mb-2">{t('Modals.manage-client.description')}</h6>
                <Controller
                  control={control}
                  name="description"
                  render={({ field, fieldState: { error } }) => (
                    <MLTextArea style={{ width: '100%' }} error={error?.message} {...field} />
                  )}
                />
              </div>

              <div className="col-12">
                <Controller
                  control={control}
                  name="logo"
                  render={({ field: { value, onChange, ...fieldProps }, fieldState: { error } }) => (
                    <MLInput
                      {...fieldProps}
                      type="file"
                      label={t('Modals.manage-client.logo')}
                      placeholder="Logo"
                      error={error?.message}
                      value={undefined}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        onChange(file);
                      }}
                    />
                  )}
                />
              </div>

              <div className="col-12 d-flex justify-content-end mt-3">
                <Button className="btn btn-ghost-success" type="submit">
                  {t('Modals.manage-client.create-btn')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Container>
    </>
  );
}

export default PestCompanyModal;
